import './navigation.scss';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

class Navigation {
    constructor (element, options) {
        const defaults = {
            initAttr: 'data-nav',
            rootToggleAttr: 'rootToggle',
            itemsAttr: 'items',
            scrollerAttr: 'scroller',
            groupAttr: 'data-nav-group',
            toggleAttr: 'toggle',
            levelAttr: 'data-nav-level',
            breakpoint: 1280
        };

        this.settings = Object.assign({}, defaults, options);

        this.$navigation = element;
        this.$navigationToggle = document.querySelector('[' + this.settings.initAttr + '="' + this.settings.rootToggleAttr + '"]');
        this.$navigationScroller = this.$navigation.querySelector('[' + this.settings.initAttr + '="' + this.settings.scrollerAttr + '"]');
        this.$navigationToggles = this.$navigation.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.toggleAttr + '"]');
        this.$navigationMainItems = this.$navigation.querySelectorAll('a.main-nav__item--level-1[data-nav-item="1"]');
        this.$body = document.querySelector('body');
        this.$siteheader = document.querySelector('[data-siteheader="root"]');
        this.currentLevel = 0;
        this.breakpoint = window.matchMedia('(min-width: ' + this.settings.breakpoint + 'px)');

        this.initialize();
    }

    initialize () {
        this.setFirstLevel();
        this.setEvents();
        this.handleTabIndices();
    }

    isOpen (button) {
        return button.getAttribute('aria-expanded') === 'true';
    }

    open (button) {
        button.setAttribute('aria-expanded', 'true');
        this.toggleSubmenuVisibility(button, true);
    }

    close (button) {
        button.setAttribute('aria-expanded', 'false');
        this.toggleSubmenuVisibility(button, false);
    }

    /**
     * Toggles inert on the wrapper of the submenu controlled by a toggle.
     * Submenus (main-nav__wrapper--level-2 / --level-3) stay inert until their parent
     * toggle is expanded, so their scroll containers and focusable children are not
     * reachable by keyboard or assistive technologies while not visible (WCAG 2.2).
     * inert alone is sufficient — aria-hidden on a parent of focusable elements would
     * itself be a WCAG violation.
     */
    toggleSubmenuVisibility (button, visible) {
        const controlsId = button.getAttribute('aria-controls');

        if (!controlsId) {
            return;
        }

        const $submenu = document.getElementById(controlsId);

        if (!$submenu) {
            return;
        }

        const $wrapper = $submenu.querySelector(':scope > .main-nav__wrapper--level-2, :scope > .main-nav__wrapper--level-3');

        if (!$wrapper) {
            return;
        }

        if (visible) {
            $wrapper.removeAttribute('inert');
        } else {
            $wrapper.setAttribute('inert', '');
        }
    }

    setEvents () {
        this.$navigation.addEventListener('click', (e) => {
            // Main nav toggle buttons (data-nav="toggle")
            const $toggle = e.target.closest('[' + this.settings.initAttr + '="' + this.settings.toggleAttr + '"]');
            if ($toggle) {
                e.preventDefault();
                this.handleToggleClick($toggle);
                return;
            }

            // Back buttons and desktop nav proxy buttons ([data-nav-for])
            const $navFor = e.target.closest('[data-nav-for]');
            if ($navFor) {
                const targetId = $navFor.getAttribute('data-nav-for');
                const $targetToggle = document.getElementById(targetId);
                if ($targetToggle) {
                    e.preventDefault();
                    this.handleToggleClick($targetToggle);
                }
            }
        });

        // Root navigation toggle button (outside navigation element)
        if (this.$navigationToggle) {
            this.$navigationToggle.addEventListener('click', () => {
                if (this.isOpen(this.$navigationToggle)) {
                    this.closeMainnav();
                } else {
                    this.openMainnav();
                }
            });
        }

        // Focus handlers for desktop nav link items (a.main-nav__item--level-1)
        this.$navigationMainItems.forEach(($mainItem) => {
            const mainItemId = $mainItem.getAttribute('data-nav-id');

            $mainItem.addEventListener('focus', () => {
                if ($mainItem.matches(':focus-visible')) {
                    this.toggleLinkFocus(mainItemId, true);
                }
            });

            $mainItem.addEventListener('blur', () => {
                this.toggleLinkFocus(mainItemId, false);
            });
        });

        // Close navigation on outside click
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('navigation')) {
                this.closeMainnav();
            }
        });

        // Close navigation on ESC key
        document.addEventListener('keydown', (e) => {
            const isEscape = e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27;
            if (isEscape && this.$navigationToggle && this.isOpen(this.$navigationToggle)) {
                this.closeMainnav();
            }
        });

        // Close navigation on resize
        window.addEventListener('resize', () => {
            // this.closeMainnav();
        });
    }

    handleToggleClick ($toggle) {
        const wasOpen = this.isOpen($toggle);
        const elementLevel = parseInt($toggle.getAttribute(this.settings.levelAttr));

        this.$body.classList.remove('nav-level--' + this.currentLevel);
        this.$navigationScroller.scrollTo(0, 0);

        if (!wasOpen) {
            this.open($toggle);
            this.currentLevel = elementLevel + 1;

            // If a level-1 item is opened and root nav is closed, open root nav
            if (elementLevel === 1 && this.$navigationToggle && !this.isOpen(this.$navigationToggle)) {
                this.openMainnav();
            }

            this.closeOthers($toggle);
            this.addSubTabIndices($toggle);
        } else {
            this.close($toggle);
            this.currentLevel = elementLevel;
            this.closeSubnav($toggle);
            this.removeSubTabIndices($toggle);
            $toggle.focus();
        }

        this.$body.classList.add('nav-level--' + this.currentLevel);
    }

    openMainnav () {
        this.open(this.$navigationToggle);
        this.$siteheader?.classList.add('siteheader--is-active');
        disableBodyScroll(this.$navigationScroller);
        if (!this.breakpoint.matches) {
            this.addFirstLevelIndices();
        }
    }

    closeMainnav () {
        this.close(this.$navigationToggle);
        this.$siteheader?.classList.remove('siteheader--is-active');
        this.clearAllSubnavs();
        this.handleTabIndices();
        clearAllBodyScrollLocks();
        this.$navigationToggle.focus();
    }

    closeSubnav ($currentToggle) {
        const $currentNav = $currentToggle.closest('[' + this.settings.groupAttr + '="' + this.currentLevel + '"]');
        const subLevel = this.currentLevel + 1;
        const $subToggles = $currentNav.querySelectorAll('[' + this.settings.levelAttr + '="' + subLevel + '"]');

        $subToggles.forEach($t => {
            if (this.isOpen($t)) this.close($t);
        });
    }

    closeOthers ($currentToggle) {
        const $currentNav = $currentToggle.closest('[' + this.settings.initAttr + '="' + this.settings.itemsAttr + '"]');
        const $siblings = $currentNav.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.toggleAttr + '"]');

        $siblings.forEach($t => {
            if ($t !== $currentToggle && this.isOpen($t)) this.close($t);
        });
    }

    clearAllSubnavs () {
        this.$navigationToggles.forEach($toggle => {
            if (this.isOpen($toggle)) this.close($toggle);
        });

        // Hide all back handles
        this.$navigation.querySelectorAll('[data-main-nav="back-handle"]').forEach($back => {
            $back.setAttribute('tabindex', -1);
        });

        for (let level = 0; level < 5; level++) {
            this.$body.classList.remove('nav-level--' + level);
        }

        this.setFirstLevel();
    }

    setFirstLevel () {
        this.currentLevel = 1;
        this.$body.classList.add('nav-level--' + this.currentLevel);
    }

    toggleLinkFocus (id, focus) {
        const $links = this.$navigation.querySelectorAll('[data-nav-id="' + id + '"]');
        $links.forEach($link => {
            if (focus) {
                $link.classList.add('focus-visible');
            } else {
                $link.classList.remove('focus-visible');
            }
        });
    }

    handleTabIndices () {
        this.hideAllTabIndices(this.$navigationToggles);
        this.hideAllTabIndices(this.$navigation.querySelectorAll('a'));

        // Hide all back button handles initially (shown when submenu opens)
        this.$navigation.querySelectorAll('[data-main-nav="back-handle"]').forEach($back => {
            $back.setAttribute('tabindex', -1);
        });

        if (this.breakpoint.matches) {
            this.addFirstLevelIndices();
        }
    }

    addFirstLevelIndices () {
        const $mainToggles = this.$navigation.querySelectorAll('[data-nav-level="1"]');
        this.showAllTabIndices($mainToggles);
        const $mainItems = this.$navigation.querySelectorAll('.main-nav__item--level-1');
        this.showAllTabIndices($mainItems);
    }

    hideAllTabIndices ($elements) {
        $elements.forEach($el => {
            $el.setAttribute('tabindex', -1);
        });
    }

    /**
     * Show element in keyboard navigation.
     * Labels need explicit tabindex="0" — removeAttribute() does not make them focusable.
     * Buttons and <a> handle differently: <a> uses removeAttribute; buttons use setAttribute(0).
     */
    showAllTabIndices ($elements) {
        $elements.forEach($el => {
            if ($el.tagName.toLowerCase() === 'a') {
                $el.removeAttribute('tabindex');
            } else {
                $el.setAttribute('tabindex', 0);
            }
        });
    }

    addSubTabIndices ($currentToggle) {
        const $subItems = this.getChildNavigation($currentToggle);
        this.showAllTabIndices($subItems);

        // Show back handle in the opened submenu
        const currentNavLevel = parseInt($currentToggle.getAttribute(this.settings.levelAttr));
        const $currentNavGroup = $currentToggle.closest('[' + this.settings.groupAttr + '="' + currentNavLevel + '"]');
        $currentNavGroup.querySelectorAll('[data-main-nav="back-handle"]').forEach($back => {
            $back.setAttribute('tabindex', 0);
        });
    }

    removeSubTabIndices ($currentToggle) {
        const $subItems = this.getChildNavigation($currentToggle);
        this.hideAllTabIndices($subItems);

        // Hide back handle in the closed submenu
        const currentNavLevel = parseInt($currentToggle.getAttribute(this.settings.levelAttr));
        const $currentNavGroup = $currentToggle.closest('[' + this.settings.groupAttr + '="' + currentNavLevel + '"]');
        $currentNavGroup.querySelectorAll('[data-main-nav="back-handle"]').forEach($back => {
            $back.setAttribute('tabindex', -1);
        });
    }

    getChildNavigation ($currentToggle) {
        const currentNavLevel = parseInt($currentToggle.getAttribute('data-nav-level'));
        const childNavLevel = currentNavLevel + 1;
        const $currentNav = $currentToggle.closest('[' + this.settings.groupAttr + '="' + currentNavLevel + '"]');
        const $childItems = $currentNav.querySelectorAll('[data-nav-level="' + childNavLevel + '"], [data-nav-item="' + childNavLevel + '"]');

        return $childItems;
    }
}

export default Navigation;

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context === document) {
        const $navigation = document.querySelector('[data-nav="root"]');
        if ($navigation) {
            const navigationAPI = new Navigation($navigation);
            $navigation.API = navigationAPI;
        }
    }
});
